import React from 'react';
import Icon from '@ifit/react-icons/dist/components/icon';
import NativeClick, { METHODS } from '@ifit/mp-native-click';
import cn from 'classnames';
import { withTranslation, II18n } from '@ifit/rapport-webview';
import SlideShow from '@ifit/mp-slideshow';
import Estimates from '@ifit/mp-estimates';
import FeaturedContentSection, {
  IFeaturedContentSection,
} from '@ifit/mp-featured-content-section';
import ProgressiveBackground from '@ifit/mp-progressive-background';
import LONG_ARROW_RIGHT_LINE from '@ifit/react-icons/dist/icons/LONG_ARROW_RIGHT_LINE';
import Loading from '@ifit/mp-loading';
import Chip from '@ifit/mp-chip';
import Intensity from '@ifit/mp-intensity';
import { withTheme } from '@ifit/themes/withTheme';
import rapportPackage from './rapport-web.json';

import { white, shark } from '@ifit/base-coat/js/colors';
import './index.scss';
import * as util from './utilities';

interface IOwnProps {
  featuredItems: Array<IContentItems | IFeaturedContentSection>;
  equipmentType?: string;
  deviceType?: string;
  isLoading: boolean;
  i18n: II18n;
  useMetricUnits: boolean;
  classNames: string;
  themeClass: string;
  referralSource?: string;
  language?: string;
}

export interface IContentItems {
  type: 'workout' | 'program' | 'section' | 'challenge' | 'live-workout';
  items: Array<IItems>;
  ids: Array<string>;
}

interface IItems {
  item: IItem;
  type: string;
}

interface IItem {
  itemId: string;
  title: string;
  locked: boolean;
  intensity: number;
  rating: Array<IRating>;
  estimates: any;
  image: string;
  trainerImage: string;
}

interface IRating {
  average: number;
}

interface IProps extends IOwnProps {}

export const PureFeaturedContent: React.FC<IProps> = ({
  featuredItems,
  isLoading,
  useMetricUnits,
  classNames = '',
  i18n,
  themeClass,
  equipmentType,
  deviceType,
  language,
}: IProps) => {
  if (featuredItems && featuredItems.length === 0) {
    return null;
  }

  const iconColor = {
    'light-theme': white,
    'dark-theme': white,
    'vault-theme': shark,
  }[themeClass];

  const isFeaturedContentSection = (thing: any): boolean =>
    'title' in thing && thing.type === 'section';

  const isChallenge = slide => {
    const challengeEndTimestamp = slide.items[0].item.challengeEndTimestamp;
    return slide.type === 'challenge' || !!challengeEndTimestamp;
  };

  const featuredItem: any = featuredItems?.[0];

  return (
    <div
      className={cn(
        'mp-featured-content',
        classNames,
        themeClass,
        featuredItems && isFeaturedContentSection(featuredItem)
          ? 'mp-featured-content-section'
          : ''
      )}
    >
      {!featuredItems && !isLoading && <Loading absolute />}
      {featuredItems && (
        <>
          {isFeaturedContentSection(featuredItem) && (
            <FeaturedContentSection
              featuredItem={featuredItem}
              i18n={i18n}
              equipmentType={equipmentType}
              useMetricUnits={useMetricUnits}
              themeClass={themeClass}
              deviceType={deviceType}
            ></FeaturedContentSection>
          )}
          {!isFeaturedContentSection(featuredItems[0]) && (
            <SlideShow data={featuredItems} animation="slide-in">
              {slide => {
                const featuredContent = slide.items[0];
                const featuredContentItem = featuredContent.item;
                const {
                  isLiveEvent,
                  isLiveEventAiring,
                  airDate,
                } = util.getLiveEventProps(featuredContentItem);

                return (
                  <>
                    <NativeClick
                      objectToSend={
                        slide.type === 'workout' ||
                        slide.type === 'live-workout'
                          ? METHODS.workoutSelected(featuredContentItem.itemId)
                          : METHODS.programSelected(featuredContentItem.itemId)
                      }
                    >
                      <div className={`spotlight ${slide.type}`}>
                        {featuredContentItem.image && (
                          <ProgressiveBackground
                            resizeOptions={{ useStandardDimensions: false }}
                            image={featuredContentItem.image}
                          />
                        )}
                        <div className="image-overlay" />

                        <div className="content">
                          {slide.type === 'program' &&
                            featuredContentItem.workouts &&
                            featuredContentItem.workouts.length !== 0 && (
                              <div className="featured-content-chip">
                                <Chip>
                                  {util.numberOfWorkoutsLabel(
                                    i18n,
                                    featuredContentItem.workouts.length
                                  )}
                                </Chip>
                              </div>
                            )}
                          {slide.type === 'workout' &&
                            featuredContentItem.category && (
                              <div className="featured-content-chip">
                                <Chip>{featuredContentItem.category}</Chip>
                              </div>
                            )}
                          {slide.type === 'live-workout' &&
                            isLiveEvent &&
                            !isLiveEventAiring && (
                              <div className="featured-upcoming-date-chip">
                                <Chip>
                                  {util.viewUpcomingLiveEventChipLabel(
                                    i18n,
                                    util.getWeekDay(airDate, language),
                                    airDate.toLocaleDateString(language),
                                    util.getTime(airDate, language)
                                  )}
                                </Chip>
                              </div>
                            )}
                          {slide.type === 'live-workout' && isLiveEventAiring && (
                            <div className="featured-airing-date-chip">
                              <Chip>
                                {util.viewAiringLiveEventChipLabel(i18n)}
                              </Chip>
                            </div>
                          )}
                          {slide.type === 'program' &&
                            featuredContentItem.intensity && (
                              <Intensity
                                intensity={featuredContentItem.intensity}
                              />
                            )}
                          {(slide.type === 'challenge' ||
                            featuredContent.type === 'challenge') &&
                            featuredContentItem.intensity && (
                              <Intensity
                                intensity={featuredContentItem.intensity}
                              />
                            )}

                          <h1>{featuredContentItem.title}</h1>

                          <Estimates
                            estimates={featuredContentItem.estimates}
                            iconColor={iconColor}
                            useMetricUnits={useMetricUnits}
                          />

                          <button className="featured-content-link">
                            {isChallenge(slide)
                              ? util.viewChallengeButtonLabel(i18n)
                              : slide.type === 'program'
                              ? util.viewSeriesButtonLabel(i18n)
                              : slide.type === 'live-workout'
                              ? util.viewLiveEventButtonLabel(i18n)
                              : util.viewWorkoutButtonLabel(i18n)}
                            <Icon
                              icon={LONG_ARROW_RIGHT_LINE}
                              color={iconColor}
                              size={20}
                            ></Icon>
                          </button>
                        </div>
                      </div>
                    </NativeClick>
                  </>
                );
              }}
            </SlideShow>
          )}
        </>
      )}
    </div>
  );
};

export default withTranslation(rapportPackage)(
  withTheme<IProps>(PureFeaturedContent)
);
